import * as React from 'react';
import { Redirect } from 'react-router-dom';
import { defaultMemoize } from 'reselect';
import * as _ from 'underscore';
import { Messages } from '../../../codegen/Localize';
import { AngularDirective } from '../../../common/react/components/AngularDirective';
import { Location } from '../../../common/react/Location';
import { schedulePath, scheduleSubspaceNameMatchesSchedule } from '../../../schedule/utils/ScheduleUtils';
import { ISchedulePlaceScope } from '../../../ts/controllers/ScheduleSpaceDetailsCtrl';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { ExtractTaskDefinition, RunFlowTaskDefinition, SubscriptionDefinition } from '../../../ts/models/ContentDefinitions';
import { IScheduleInfo } from '../../../ts/models/IScheduleInfo';
import { ScheduleActions } from '../../../ts/ServerApi/types/ISchedule';
import { ServerUrls } from '../../ServerUrls';
import { ISpaceInfoTypeItemCount } from '../../state/ContentCountsState';
import { ScheduleSubspaceName, SpaceInfo } from '../../state/SpaceInfo';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { explorerSubspace } from '../SubspaceHelpers';
import { Subspace, TabbedSpace } from '../TabbedSpace';
import { ScheduleSpaceHeader } from './ScheduleSpaceHeader';


export interface ScheduleSpaceStateProps {
  readonly spaceInfo: SpaceInfo;
  readonly contentCounts: ISpaceInfoTypeItemCount<'schedule'>;
  readonly contentCountsUpToDate: boolean;
  readonly displaySchedulesInClientTimezone: boolean;
}

export interface ScheduleSpaceDispatchProps {
  readonly fetchUpdatedContentCounts: () => void;
}

export interface ScheduleSpaceOwnProps extends ReactDirectiveProps {
  readonly urls: SpacesSiteUrls | ServerUrls;
  readonly reloadSpace: () => void;
}

export type ScheduleSpaceProps = ScheduleSpaceStateProps & ScheduleSpaceDispatchProps & ScheduleSpaceOwnProps;

export class ScheduleSpace extends React.PureComponent<ScheduleSpaceProps> {

  private memoizedGetSiteUserSubspaces = defaultMemoize(getScheduleSubspaces);

  public componentDidUpdate(): void {
    this.props.fetchUpdatedContentCounts();
  }

  public componentDidMount(): void {
    this.props.fetchUpdatedContentCounts();
  }

  private static scheduleUrlBySubspace: Record<ScheduleSubspaceName, (urls: SpacesSiteUrls | ServerUrls, scheduleId: string) => string> = {
    flows: (urls, scheduleId) => urls.scheduleFlowsUrl(scheduleId),
    subscriptions: (urls, scheduleId) =>  urls.scheduleSubscriptionsUrl(scheduleId),
    extractRefreshes: (urls, scheduleId) =>  urls.scheduleExtractRefreshesUrl(scheduleId),
    details: (urls, scheduleId) =>  urls.scheduleDetailsUrl(scheduleId),
  };

  private getScheduleUserUrl = (subspaceName?: ScheduleSubspaceName) => {
    let url = this.props.urls.scheduleUrl(this.props.spaceInfo.item.id);
    if (subspaceName) {
      const urlFn = ScheduleSpace.scheduleUrlBySubspace[subspaceName];
      url = urlFn && urlFn(this.props.urls, this.props.spaceInfo.item.id) || url;
    }
    return url;
  }

  public render() {
    const schedule = this.props.spaceInfo.item as IScheduleInfo;

    // we redirect if the subspacename doesn't match the scheduled action to the correct one
    if (this.props.spaceInfo.subspaceName !== 'details' && !scheduleSubspaceNameMatchesSchedule(this.props.spaceInfo.subspaceName as ScheduleSubspaceName, schedule.scheduledAction)) {
      return <Redirect to={schedulePath(this.props.urls, schedule.id, schedule.scheduledAction)}/>;
    }

    const subspaces = this.memoizedGetSiteUserSubspaces(
      this.props.angularContext,
      this.props.contentCounts,
      this.props.contentCountsUpToDate,
      schedule,
      this.navigateToAllSchedules,
      this.props.reloadSpace
    );

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.spaceInfo.subspaceName}
        urlForSubspace={this.getScheduleUserUrl}
        mastheadElement={this.mastheadElement()}
        testIdPrefix={'schedule'}
        spaceItemName={schedule.name}
      />
    );
  }

  private mastheadElement(): React.ReactNode {
    return <ScheduleSpaceHeader
      item={this.props.spaceInfo.item as IScheduleInfo}
      displaySchedulesInClientTimezone={this.props.displaySchedulesInClientTimezone}
    />;
  }

  private navigateToAllSchedules = () => {
    Location.instance.setUrl(this.props.urls.schedulesUrl());
  }
}

function getScheduleSubspaces(
  angularContext: AngularContext,
  contentCounts: ISpaceInfoTypeItemCount<'schedule'>,
  contentCountsUpToDate: boolean,
  schedule: IScheduleInfo,
  navigateToAllSchedules: () => void,
  reloadSpace: () => void
): ReadonlyArray<Subspace> {
  return _.compact([
    schedule.scheduledAction === ScheduleActions.Extract && explorerSubspace({
      name: 'extractRefreshes',
      tabLabel: Messages.extractTasks(),
      supportedDisplayModes: ExtractTaskDefinition.supportedDisplayModes,
      supportFilterPanel: false,
      angularContext,
      contentCount: contentCounts && contentCounts['extractRefreshes'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
    }),
    schedule.scheduledAction === ScheduleActions.Subscription && explorerSubspace({
      name: 'subscriptions',
      tabLabel: Messages.subscriptions(),
      supportedDisplayModes: SubscriptionDefinition.supportedDisplayModes,
      supportFilterPanel: false,
      angularContext,
      contentCount: contentCounts && contentCounts['subscriptions'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
    }),
    schedule.scheduledAction === ScheduleActions.Flow && explorerSubspace({
      name: 'flows',
      tabLabel: Messages.flows(),
      supportedDisplayModes: RunFlowTaskDefinition.supportedDisplayModes,
      supportFilterPanel: false,
      angularContext,
      contentCount: contentCounts && contentCounts['flows'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled'
    }),
    {
      name: 'details',
      tabLabel: Messages.details(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <DetailsSubspace
          angularContext={angularContext}
          schedule={schedule}
          navigateToAllSchedules={navigateToAllSchedules}
          reloadSpace={reloadSpace}
        />
      )
    }
  ]);
}

const template = `<div ng-controller="ScheduleSpaceDetailsCtrl" ng-include="'scheduleDetails.html'"></div>`;

export interface DetailsSubspaceProps {
  readonly angularContext: AngularContext;
  readonly schedule: IScheduleInfo;
  readonly navigateToAllSchedules: () => void;
  readonly reloadSpace: () => void;
}

export class DetailsSubspace extends React.PureComponent<DetailsSubspaceProps> {

  private childScheduleScope: ISchedulePlaceScope = undefined;
  public componentDidUpdate() {
    if (!_.isUndefined(this.childScheduleScope)) {
      this.childScheduleScope.schedule = this.props.schedule;
      this.childScheduleScope.$apply();
    }
  }
  public componentDidMount() {
    if (!_.isUndefined(this.childScheduleScope)) {
      this.childScheduleScope.$apply();
    }
  }

  public render() {
    // the parent div here is important to ensure AngularDirective plays nice when unmounting
    return (
      <div>
        <AngularDirective
          template={template}
          angularContext={this.props.angularContext}
          setupScope={this.setupScope}
        />
      </div>
    );
  }

  private setupScope = (scope: ng.IScope) => {
    const childScope = scope.$new() as ISchedulePlaceScope;
    const description = IScheduleInfo.getScheduleDescription(this.props.schedule);
    this.childScheduleScope = childScope;
    childScope.schedule = this.props.schedule;
    childScope.scheduleDescription = description;
    childScope.navigateToAllSchedules = this.props.navigateToAllSchedules;
    childScope.reloadSpace = this.props.reloadSpace;
    return childScope;
  }
}
